// Material protegido: el estudiante solo ve/escucha el contenido dentro de la
// página (sin botones de descarga, sin clic derecho, sin arrastrar). Las URLs de
// R2 se resuelven a URLs firmadas de corta duración vía Cloudflare Worker. Los
// PDF se renderizan en línea con pdf.js (proxy del Worker, CORS *) en lugar de
// enlazarse directamente.
import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { ChevronLeft, ChevronRight, FileText, Loader2, AlertTriangle } from 'lucide-react';
import { signUrl, pdfProxyUrl } from '../../services/mediaSigner';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url,
).toString();

const blockEvent = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

// Resuelve src a URL firmada; mientras carga conserva el valor original (que
// fallará si el bucket está privado, pero evita parpadeos).
export const ProtectedVideo = ({ src, style, className }) => {
  const [resolved, setResolved] = useState(src);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    signUrl(src).then((u) => { if (!cancelled) setResolved(u); });
    return () => { cancelled = true; };
  }, [src]);

  if (failed || !src) {
    return (
      <div
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px',
          width: '100%', aspectRatio: '16/9', borderRadius: '10px', background: 'rgba(10,26,42,0.6)',
          border: '1px solid rgba(239,68,68,0.25)', color: '#94a3b8', ...style,
        }}
        onContextMenu={blockEvent}
      >
        <AlertTriangle size={22} style={{ color: '#f87171', opacity: 0.7 }} />
        <span style={{ fontSize: '0.74rem', fontWeight: 600 }}>Video no disponible (404)</span>
      </div>
    );
  }

  return (
    <video
      className={className}
      style={style}
      src={resolved}
      controls
      controlsList="nodownload"
      disablePictureInPicture
      draggable={false}
      preload="metadata"
      onError={() => setFailed(true)}
      onContextMenu={blockEvent}
      onDragStart={blockEvent}
      title="Video del curso"
      aria-label="Video del curso"
    >
      Tu navegador no soporta reproducción de video HTML5.
    </video>
  );
};

export const ProtectedAudio = ({ src, style }) => {
  const [resolved, setResolved] = useState(src);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    signUrl(src).then((u) => { if (!cancelled) setResolved(u); });
    return () => { cancelled = true; };
  }, [src]);

  if (failed || !src) {
    return (
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          padding: '14px', borderRadius: '10px', background: 'rgba(10,26,42,0.6)',
          border: '1px solid rgba(239,68,68,0.25)', color: '#94a3b8', ...style,
        }}
        onContextMenu={blockEvent}
      >
        <AlertTriangle size={16} style={{ color: '#f87171', opacity: 0.7 }} />
        <span style={{ fontSize: '0.74rem', fontWeight: 600 }}>Podcast no disponible (404)</span>
      </div>
    );
  }

  return (
    <audio
      controls
      controlsList="nodownload"
      src={resolved}
      preload="none"
      style={style}
      onError={() => setFailed(true)}
      onContextMenu={blockEvent}
      onDragStart={blockEvent}
    >
      Tu navegador no soporta reproducción de audio.
    </audio>
  );
};

export const ProtectedImage = ({ src, alt = 'Imagen de apoyo', caption, style }) => {
  const [resolved, setResolved] = useState(src);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    signUrl(src).then((u) => { if (!cancelled) setResolved(u); });
    return () => { cancelled = true; };
  }, [src]);
  return (
    <div
      style={{ position: 'relative', userSelect: 'none', WebkitUserSelect: 'none', ...style }}
      onContextMenu={blockEvent}
    >
      {failed || !src ? (
        <div
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px',
            width: '100%', minHeight: '120px', borderRadius: '10px', background: 'rgba(10,26,42,0.6)',
            border: '1px solid rgba(239,68,68,0.25)', color: '#94a3b8',
          }}
        >
          <AlertTriangle size={18} style={{ color: '#f87171', opacity: 0.7 }} />
          <span style={{ fontSize: '0.72rem', fontWeight: 600 }}>Imagen no disponible (404)</span>
        </div>
      ) : (
        <img
          src={resolved}
          alt={alt}
          draggable={false}
          onError={() => setFailed(true)}
          onDragStart={blockEvent}
          style={{
            width: '100%', display: 'block', borderRadius: '10px',
            border: '1px solid rgba(197,160,89,0.2)',
            pointerEvents: 'none', userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none',
          }}
        />
      )}
      {/* Overlay transparente que captura clic derecho / arrastre sobre la imagen */}
      <div
        style={{ position: 'absolute', inset: 0, cursor: 'default', userSelect: 'none', WebkitUserSelect: 'none' }}
        onContextMenu={blockEvent}
        onMouseDown={blockEvent}
      />
      {caption && (
        <span style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', marginTop: '6px', userSelect: 'none' }}>
          {caption}
        </span>
      )}
    </div>
  );
};

// Enlace a un recurso R2 (p.ej. análisis) usando URL firmada: requiere sesión y
// la URL expira; no se enlaza el objeto público directo.
export const ProtectedLink = ({ href, children, style }) => {
  const [resolved, setResolved] = useState(href);
  useEffect(() => {
    let cancelled = false;
    signUrl(href).then((u) => { if (!cancelled) setResolved(u); });
    return () => { cancelled = true; };
  }, [href]);
  return (
    <a href={resolved} target="_blank" rel="noopener noreferrer" style={style}>
      {children}
    </a>
  );
};

// Visor de PDF embebido: no existe enlace directo al archivo ni botón de
// descarga; las páginas se dibujan en <canvas> con pdf.js (paginación simple).
// La URL pasa por el proxy del Worker (CORS *), no se expone el objeto directo.
export const InlinePdf = ({ url, name, style, book = false }) => {
  const canvasRef = useRef(null);
  const bodyRef = useRef(null);
  const boxRef = useRef(null);
  const [boxH, setBoxH] = useState(0);
  const [area, setArea] = useState({ w: 0, h: 0 });
  const [doc, setDoc] = useState(null);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [proxyUrl, setProxyUrl] = useState(null);

  // Resuelve la URL a su proxy del Worker (no R2 directo).
  useEffect(() => {
    let cancelled = false;
    setProxyUrl(null);
    setLoading(true);
    setError('');
    setPage(1);
    setNumPages(0);
    setDoc(null);
    Promise.resolve(pdfProxyUrl(url)).then((u) => { if (!cancelled) setProxyUrl(u); });
    return () => { cancelled = true; };
  }, [url]);

  // Modo libro: el visor ocupa toda la altura restante de la pantalla.
  useEffect(() => {
    if (!book) return undefined;
    const el = boxRef.current;
    if (!el) return undefined;
    const remeasure = () => {
      const top = el.getBoundingClientRect().top;
      const viewport = window.innerHeight || document.documentElement.clientHeight || 800;
      const h = Math.max(480, Math.min(Math.round(viewport - top - 8), viewport - 24));
      setBoxH((prev) => (prev === h ? prev : h));
    };
    remeasure();
    const raf1 = requestAnimationFrame(remeasure);
    const raf2 = requestAnimationFrame(() => requestAnimationFrame(remeasure));
    const timers = [
      setTimeout(remeasure, 120),
      setTimeout(remeasure, 350),
    ];
    window.addEventListener('resize', remeasure);
    window.addEventListener('orientationchange', remeasure);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', remeasure);
      window.removeEventListener('orientationchange', remeasure);
    };
  }, [book]);

  // Modo libro: mide el área disponible para que la página completa quepa en pantalla.
  useEffect(() => {
    if (!book) return undefined;
    const el = bodyRef.current;
    if (!el) return undefined;
    const measure = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      setArea((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [book]);

  // Modo libro: navegación con teclado (flechas / PageUp / PageDown / Inicio / Fin).
  useEffect(() => {
    if (!book || numPages <= 1) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'PageDown') setPage((p) => Math.min(numPages, p + 1));
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') setPage((p) => Math.max(1, p - 1));
      else if (e.key === 'Home') setPage(1);
      else if (e.key === 'End') setPage(numPages);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [book, numPages]);

  useEffect(() => {
    if (!proxyUrl) return undefined;
    let cancelled = false;
    let pdf = null;
    setLoading(true);
    setError('');
    setPage(1);
    setNumPages(0);
    pdfjsLib.getDocument({ url: proxyUrl })
      .promise
      .then((d) => { if (!cancelled) { pdf = d; setDoc(d); setNumPages(d.numPages); } })
      .catch((e) => { if (!cancelled) setError(e?.message || 'No se pudo cargar el documento.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; if (pdf) { try { pdf.destroy(); } catch (_) { /* noop */ } } };
  }, [proxyUrl]);

  // Dibuja la página actual en el canvas cuando cambia doc/página.
  useEffect(() => {
    if (!doc || loading || error) return undefined;
    let cancelled = false;
    let task = null;
    doc.getPage(page)
      .then((pg) => {
        if (cancelled) return null;
        const el = canvasRef.current;
        if (!el) return null;
        const wrap = bodyRef.current;
        const inner = 12; // padding del área de lectura
        const availW = Math.max(200, (area.w || wrap?.clientWidth || 920) - inner);
        const baseViewport = pg.getViewport({ scale: 1 });
        const widthScale = availW / baseViewport.width;
        let scale = widthScale;
        if (book) {
          const availH = Math.max(200, (area.h || wrap?.clientHeight || 640) - inner);
          scale = Math.min(widthScale, availH / baseViewport.height);
        }
        const vp = pg.getViewport({ scale });
        el.width = Math.floor(vp.width);
        el.height = Math.floor(vp.height);
        task = pg.render({ canvasContext: el.getContext('2d', { willReadFrequently: true }), viewport: vp });
        return task.promise;
      })
      .catch((e) => {
        if (cancelled || e?.name === 'RenderingCancelledException') return;
        setError('No se pudo renderizar la página.');
      });
    return () => {
      cancelled = true;
      if (task) { try { task.cancel(); } catch (_) { /* noop */ } }
    };
  }, [doc, page, loading, error, book, boxH, area.w, area.h]);

  const navBtn = (disabled) => ({
    padding: '6px 10px', borderRadius: '8px', cursor: disabled ? 'not-allowed' : 'pointer',
    background: disabled ? 'rgba(255,255,255,0.04)' : 'rgba(197,160,89,0.12)',
    border: disabled ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(197,160,89,0.35)',
    color: disabled ? '#475569' : '#c5a059', display: 'flex', alignItems: 'center',
  });

  const pageArrow = (side, disabled) => ({
    position: 'absolute', top: '50%', transform: 'translateY(-50%)', [side]: '12px', zIndex: 5,
    width: '48px', height: '48px', borderRadius: '50%', padding: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    background: disabled ? 'rgba(7,21,38,0.35)' : 'rgba(197,160,89,0.18)',
    border: disabled ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(197,160,89,0.5)',
    color: disabled ? '#334155' : '#c5a059',
    backdropFilter: 'blur(4px)',
    boxShadow: disabled ? 'none' : '0 4px 16px rgba(0,0,0,0.45)',
    transition: 'transform 0.15s ease, background 0.15s ease',
  });

  return (
    <div
      ref={boxRef}
      style={{
        ...style,
        border: '1px solid rgba(197,160,89,0.25)', borderRadius: '12px', overflow: 'hidden',
        background: 'rgba(7,21,38,0.5)',
        display: 'flex', flexDirection: 'column',
        ...(book ? { height: boxH ? `${boxH}px` : 'calc(100vh - 200px)', minHeight: '480px' } : null),
      }}
      onContextMenu={blockEvent}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px',
        borderBottom: '1px solid rgba(197,160,89,0.15)', background: 'rgba(10,26,42,0.6)',
      }}>
        <FileText size={14} style={{ color: '#f87171', flexShrink: 0 }} />
        <span style={{
          flex: 1, fontSize: '0.78rem', fontWeight: 700, color: '#e2e8f0',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {name || 'Documento PDF'}
        </span>
        <span style={{ fontSize: '0.66rem', color: '#64748b', flexShrink: 0 }}>solo lectura</span>
      </div>

      <div
        ref={bodyRef}
        style={{
          flex: book ? 1 : undefined, minHeight: 0, position: 'relative',
          padding: '4px 6px', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: '10px', overflow: 'hidden',
        }}
      >
        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#94a3b8', fontSize: '0.76rem', padding: '2rem 0' }}>
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Cargando documento...
          </div>
        )}
        {error && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#f87171', fontSize: '0.76rem', padding: '1rem 0' }}>
            <AlertTriangle size={16} /> {error}
          </div>
        )}
        {!loading && !error && doc && (
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%', maxHeight: book ? '100%' : undefined, height: 'auto',
              borderRadius: '4px', boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            }}
          />
        )}

        {book && !loading && !error && doc && numPages > 1 && (
          <>
            <button
              type="button" aria-label="Página anterior" style={pageArrow('left', page <= 1)}
              disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft size={22} />
            </button>
            <button
              type="button" aria-label="Página siguiente" style={pageArrow('right', page >= numPages)}
              disabled={page >= numPages} onClick={() => setPage((p) => Math.min(numPages, p + 1))}
            >
              <ChevronRight size={22} />
            </button>
          </>
        )}
      </div>

      {!loading && !error && doc && numPages > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
          padding: '8px 14px', borderTop: '1px solid rgba(197,160,89,0.15)',
          background: 'rgba(10,26,42,0.6)', flexShrink: 0,
        }}>
          <button type="button" style={navBtn(page <= 1)} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            <ChevronLeft size={14} /> <span style={{ fontSize: '0.68rem', fontWeight: 700 }}>Anterior</span>
          </button>
          <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Página {page} de {numPages}</span>
          <button type="button" style={navBtn(page >= numPages)} disabled={page >= numPages} onClick={() => setPage((p) => Math.min(numPages, p + 1))}>
            <span style={{ fontSize: '0.68rem', fontWeight: 700 }}>Siguiente</span> <ChevronRight size={14} />
          </button>
          {book && (
            <span style={{ fontSize: '0.6rem', color: '#475569' }}>
              Usa ← → para pasar página
            </span>
          )}
        </div>
      )}
    </div>
  );
};
